/*
 * KEYED (payload_version = 1) ADAPTER — the current model.
 *
 * Column contract for v1 rows:
 *   payload            { table: { ...previous DB record } }   ({} for adds)
 *   request_payload    { ...raw frontend body }               (never overwritten)
 *   updated_payload    frontend body while PENDING  ->  { table: { ...post-approve DB record } } once APPROVED
 *   payload_difference { table: { field: { old, new } } }     (written on approve)
 *
 * So there are two display cases:
 *
 *  1. APPROVED/REJECTED-after-approve — payload_difference exists. It is authoritative:
 *     computed server-side from DB record vs DB record, already keyed by table, and it
 *     covers multi-row actions (e.g. a lineitem edit touching lineitems + audience_targets).
 *     We render it verbatim; no diffing in the browser.
 *
 *  2. PENDING — nothing has been applied yet, so there is no payload_difference. We show
 *     "current vs requested" by comparing the stored before-snapshot (payload[table], a DB
 *     record) against what the maker submitted (request_payload, a frontend body).
 *     Those two are not always key-identical (e.g. cardlist sends `price` where the column
 *     is `amount`), so a submitted key with no counterpart simply shows an empty "before" —
 *     which reads correctly as "being set".
 */
import { HIDDEN_KEYS, isPlainObject } from "./constants";

const isEqual = (a, b) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);

// Case 1: render the server-computed diff as-is.
const fromPayloadDifference = (difference) =>
  Object.entries(difference)
    .map(([table, fields]) => ({
      table,
      rows: Object.entries(fields || {})
        .filter(([field]) => !HIDDEN_KEYS.includes(field))
        .map(([field, value]) => ({ field, before: value?.old ?? null, after: value?.new ?? null })),
    }))
    .filter((group) => group.rows.length);

// Case 2: pending — compare the before-snapshot against the submitted body.
const fromPendingSnapshot = (action) => {
  const before = isPlainObject(action?.payload) ? action.payload : {};
  const submitted = action?.request_payload ?? action?.updated_payload ?? {};
  if (!isPlainObject(submitted)) return [];

  // Adds have an empty before-snapshot, so fall back to the action's own target table.
  const tables = Object.keys(before);
  if (!tables.length && action?.ref_table_name) tables.push(action.ref_table_name);
  if (!tables.length) return [];

  return tables
    .map((table) => {
      const beforeRecord = isPlainObject(before[table]) ? before[table] : {};
      // Multi-row bodies may nest per table; otherwise the body applies to the one table.
      const afterRecord = isPlainObject(submitted[table]) ? submitted[table] : submitted;

      const fields = new Set([...Object.keys(beforeRecord), ...Object.keys(afterRecord)]);
      const rows = [...fields]
        .filter((field) => !HIDDEN_KEYS.includes(field))
        .filter((field) => !isEqual(beforeRecord[field], afterRecord[field]))
        // Only surface fields the maker actually submitted. Columns that exist on the DB
        // record but were never sent (internal ids, timestamps) are not "changes".
        .filter((field) => Object.prototype.hasOwnProperty.call(afterRecord, field))
        .map((field) => ({ field, before: beforeRecord[field] ?? null, after: afterRecord[field] ?? null }));

      return { table, rows };
    })
    .filter((group) => group.rows.length);
};

export const buildKeyedGroups = (action) => {
  const difference = action?.payload_difference;
  if (isPlainObject(difference) && Object.keys(difference).length) {
    return fromPayloadDifference(difference);
  }
  return fromPendingSnapshot(action);
};
